// The owner's colour palette (Admin → Colours): every colour role in styles/theme.css that's worth changing, in
// light and dark, plus the logo drawn on the page. Only what the owner changes is stored (app_settings 'palette');
// everything else keeps the theme's own value, so a change to theme.css still reaches every role nobody touched.
// The defaults below are theme.css's values, resolved (shared/palette.test.ts keeps them in step), and the logo's
// come from src/components/logo-mark.json.

export const MODES = ['light', 'dark'] as const
export type Mode = (typeof MODES)[number]

type Group = { title: string; hint?: string; roles: [role: string, label: string][] }

export const PALETTE_GROUPS = [
  {
    title: 'Page',
    roles: [
      ['background', 'Page'],
      ['foreground', 'Text'],
      ['muted-foreground', 'Quiet text'],
      ['table', 'Table under cards'],
      ['table-foreground', 'Text on the table'],
      ['table-muted', 'Quiet text on the table'],
      ['card', 'Cards'],
      ['card-foreground', 'Text on cards'],
      ['paper', 'Paper (photos and recipe cards)'],
      ['pencil', 'Handwriting'],
      ['popover', 'Menus and sheets'],
      ['popover-foreground', 'Text in menus'],
    ],
  },
  {
    title: 'Buttons',
    roles: [
      ['primary', 'Main button'],
      ['primary-foreground', 'Main button text'],
      ['secondary', 'Quiet button'],
      ['secondary-foreground', 'Quiet button text'],
      ['accent', 'Hover'],
      ['accent-foreground', 'Text on hover'],
      ['muted', 'Soft fill'],
    ],
  },
  {
    title: 'Highlights',
    hint: 'What’s chosen and where you are, and what’s coming up.',
    roles: [
      ['brand', 'Chosen (pills, tabs, switches, links)'],
      ['brand-foreground', 'Text on chosen'],
      ['brand-wash', 'Chosen wash'],
      ['warm', 'Coming up (birthday dots)'],
      ['warm-foreground', 'Coming up text'],
      ['warm-wash', 'Coming up wash'],
    ],
  },
  {
    title: 'Lines, fields and errors',
    roles: [
      ['border', 'Lines'],
      ['input', 'Field edges'],
      ['ring', 'Focus ring'],
      ['destructive', 'Errors and delete'],
    ],
  },
  {
    title: 'Logo',
    hint: 'The logo on the page and the rings around what’s new. The tab and home-screen icons keep the logo’s own colours.',
    roles: [
      ['mark-1', 'Logo start'],
      ['mark-2', 'Logo middle'],
      ['mark-3', 'Logo end'],
      ['logo-1', 'Rings 1'],
      ['logo-2', 'Rings 2'],
      ['logo-3', 'Rings 3'],
      ['logo-4', 'Rings 4'],
    ],
  },
  {
    title: 'Book covers and empty frames',
    roles: [
      ['tint-1', 'Cover 1'],
      ['tint-2', 'Cover 2'],
      ['tint-3', 'Cover 3'],
      ['tint-4', 'Cover 4'],
      ['tint-5', 'Cover 5'],
      ['tint-6', 'Cover 6'],
    ],
  },
  {
    title: 'Photo viewer',
    roles: [
      ['viewer', 'Viewer'],
      ['viewer-foreground', 'Viewer text'],
      ['viewer-muted', 'Viewer quiet text'],
    ],
  },
  {
    title: 'Circle colours',
    hint: 'The ring around your picture in each circle.',
    roles: [
      ['circle-sage', 'Sage'],
      ['circle-olive', 'Olive'],
      ['circle-ochre', 'Ochre'],
      ['circle-clay', 'Clay'],
      ['circle-rose', 'Rose'],
      ['circle-plum', 'Plum'],
      ['circle-iris', 'Iris'],
      ['circle-harbor', 'Harbor'],
    ],
  },
] as const satisfies readonly Group[]

export type PaletteRole = (typeof PALETTE_GROUPS)[number]['roles'][number][0]
export const PALETTE_ROLES = PALETTE_GROUPS.flatMap((g) => g.roles.map(([role]) => role)) as PaletteRole[]

export type Colors = Record<PaletteRole, string>
export type Palette = Record<Mode, Partial<Colors>>
export const EMPTY_PALETTE: Palette = { light: {}, dark: {} }

const LOGO = { 'mark-1': '#d85461', 'mark-2': '#8f3e79', 'mark-3': '#552c93' }
const RINGS = { 'logo-1': '#552c93', 'logo-2': '#8f3e79', 'logo-3': '#d85461', 'logo-4': '#8f3e79' }

export const DEFAULTS: Record<Mode, Colors> = {
  light: {
    background: '#ffffff',
    foreground: '#1e2022',
    'muted-foreground': '#5f6d76',
    table: '#f8f8f7',
    'table-foreground': '#1e2022',
    'table-muted': '#5d6a73',
    card: '#ffffff',
    'card-foreground': '#1e2022',
    paper: '#ffffff',
    pencil: '#5f6d76',
    popover: '#ffffff',
    'popover-foreground': '#1e2022',
    primary: '#1e2022',
    'primary-foreground': '#ffffff',
    secondary: '#f0f1f1',
    'secondary-foreground': '#1e2022',
    accent: '#e6e8e9',
    'accent-foreground': '#1e2022',
    muted: '#f0f1f1',
    brand: '#2e736e',
    'brand-foreground': '#ffffff',
    'brand-wash': '#e5f8f6',
    warm: '#c66b58',
    'warm-foreground': '#a24b39',
    'warm-wash': '#ffefeb',
    border: '#dddfe0',
    input: '#75838c',
    ring: '#2e736e',
    destructive: '#b63042',
    ...LOGO,
    ...RINGS,
    'tint-1': '#e4ddd3',
    'tint-2': '#ebe6de',
    'tint-3': '#d6cdc0',
    'tint-4': '#c9d0d4',
    'tint-5': '#efebe5',
    'tint-6': '#b9c3c9',
    viewer: '#303e4b',
    'viewer-foreground': '#f8f8f7',
    'viewer-muted': '#a9b4bb',
    'circle-sage': '#6f8f69',
    'circle-olive': '#7d7a2c',
    'circle-ochre': '#b5801f',
    'circle-clay': '#8e5f42',
    'circle-rose': '#b35c7e',
    'circle-plum': '#6b4180',
    'circle-iris': '#5b5fb0',
    'circle-harbor': '#303e4b',
  },
  dark: {
    background: '#18191a',
    foreground: '#ecebe9',
    'muted-foreground': '#a9b4bb',
    table: '#111213',
    'table-foreground': '#ecebe9',
    'table-muted': '#a9b4bb',
    card: '#1d1e20',
    'card-foreground': '#ecebe9',
    paper: '#1d1e20',
    pencil: '#b3bec5',
    popover: '#1d1e20',
    'popover-foreground': '#ecebe9',
    primary: '#ecebe9',
    'primary-foreground': '#18191a',
    secondary: '#34424f',
    'secondary-foreground': '#ecebe9',
    accent: '#3d4b58',
    'accent-foreground': '#ecebe9',
    muted: '#34424f',
    brand: '#7fbab3',
    'brand-foreground': '#18191a',
    'brand-wash': '#1f3a3b',
    warm: '#f09a87',
    'warm-foreground': '#f09a87',
    'warm-wash': '#33231f',
    border: '#2d2f31',
    input: '#8795a0',
    ring: '#7fbab3',
    destructive: '#f28a94',
    ...LOGO,
    ...RINGS,
    'tint-1': '#242628',
    'tint-2': '#2a2c2e',
    'tint-3': '#2d2f31',
    'tint-4': '#35383b',
    'tint-5': '#222426',
    'tint-6': '#303336',
    viewer: '#0e0f10',
    'viewer-foreground': '#e7e7e4',
    'viewer-muted': '#a9b4bb',
    'circle-sage': '#93b28c',
    'circle-olive': '#b3ae5a',
    'circle-ochre': '#d9a54a',
    'circle-clay': '#c08b6c',
    'circle-rose': '#e08aaa',
    'circle-plum': '#a97dbf',
    'circle-iris': '#9296de',
    'circle-harbor': '#74a2d1',
  },
}

// Six-digit hex only, the form a colour picker gives: nothing else can reach the stylesheet built from it.
export const HEX = /^#[0-9a-f]{6}$/

export const isRole = (x: string): x is PaletteRole => (PALETTE_ROLES as string[]).includes(x)

// Whatever was stored or sent, keeping only known roles with valid colours, and only where they differ from the
// default (so going back to a default is the same as never having changed it).
export function cleanPalette(x: unknown): Palette {
  const out: Palette = { light: {}, dark: {} }
  if (!x || typeof x !== 'object') return out
  for (const mode of MODES) {
    const colors = (x as Record<string, unknown>)[mode]
    if (!colors || typeof colors !== 'object') continue
    for (const [role, value] of Object.entries(colors)) {
      if (!isRole(role) || typeof value !== 'string') continue
      const hex = value.toLowerCase()
      if (HEX.test(hex) && hex !== DEFAULTS[mode][role]) out[mode][role] = hex
    }
  }
  return out
}

export const isEmpty = (p: Palette) => MODES.every((m) => Object.keys(p[m]).length === 0)

// The stylesheet that lays the owner's colours over theme.css. Light stops at :not(.dark) so a light-mode change
// never shows in dark mode, and both outrank theme.css's own :root and .dark.
export function paletteCss(palette: Palette): string {
  const p = cleanPalette(palette)
  const rules = (colors: Partial<Colors>) =>
    Object.entries(colors)
      .map(([role, hex]) => `--${role}:${hex};`)
      .join('')
  const light = rules(p.light)
  const dark = rules(p.dark)
  return (light && `html:root:not(.dark){${light}}`) + (dark && `html.dark{${dark}}`)
}

// WCAG contrast between two hex colours.
function luminance(hex: string) {
  const channel = (i: number) => {
    const c = parseInt(hex.slice(i, i + 2), 16) / 255
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
  }
  return 0.2126 * channel(1) + 0.7152 * channel(3) + 0.0722 * channel(5)
}
export function contrast(a: string, b: string) {
  const [x, y] = [luminance(a), luminance(b)]
  return (Math.max(x, y) + 0.05) / (Math.min(x, y) + 0.05)
}

// The pairs people read every day, with the contrast each needs: 4.5 for text, 3 for edges and marks. The editor
// warns when a change drops one below; the owner can still save it.
export const CHECKS: [fg: PaletteRole, bg: PaletteRole, min: number, label: string][] = [
  ['foreground', 'background', 4.5, 'Text on the page'],
  ['muted-foreground', 'background', 4.5, 'Quiet text on the page'],
  ['table-foreground', 'table', 4.5, 'Text on the table'],
  ['table-muted', 'table', 4.5, 'Quiet text on the table'],
  ['card-foreground', 'card', 4.5, 'Text on cards'],
  ['popover-foreground', 'popover', 4.5, 'Text in menus'],
  ['primary-foreground', 'primary', 4.5, 'Main button text'],
  ['secondary-foreground', 'secondary', 4.5, 'Quiet button text'],
  ['brand-foreground', 'brand', 4.5, 'Text on chosen'],
  ['brand', 'background', 4.5, 'Links on the page'],
  ['warm-foreground', 'background', 4.5, 'Coming up text'],
  ['destructive', 'background', 4.5, 'Errors on the page'],
  ['viewer-foreground', 'viewer', 4.5, 'Viewer text'],
  ['input', 'background', 3, 'Field edges'],
  ['ring', 'background', 3, 'Focus ring'],
]

export const colorsFor = (palette: Palette, mode: Mode): Colors => ({ ...DEFAULTS[mode], ...palette[mode] })

export const lowContrast = (palette: Palette, mode: Mode) => {
  const colors = colorsFor(palette, mode)
  return CHECKS.map(([fg, bg, min, label]) => ({ label, min, ratio: contrast(colors[fg], colors[bg]) })).filter(
    (x) => x.ratio < x.min,
  )
}
